import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { formatShortName } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Button, Tabs } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { useWorkspace } from '../../lib/session';
import { AppointmentPanelPage } from './AppointmentPanelPage';
import { ArchivePage } from './ArchivePage';
import { AvailabilityPage } from './AvailabilityPage';
import { EventTypeEditorPage } from './EventTypeEditorPage';
import { EventTypesPage } from './EventTypesPage';
import { ReminderChainPage } from './ReminderChainPage';
import { StatisticsPage } from './StatisticsPage';
import { WeekPage } from './WeekPage';
import { useCalendarWeek } from './calendarApi';
import styles from './CalendarModule.module.css';

export type CalendarTab = 'week' | 'archive' | 'event-types' | 'statistics';

const tabs: ReadonlyArray<{ value: CalendarTab; label: string; href: string }> = [
  { value: 'week', label: 'Kalender', href: '/calendar/week' },
  { value: 'archive', label: 'Archiv', href: '/calendar/archive' },
  { value: 'event-types', label: 'Ereignistypen', href: '/calendar/event-types' },
  { value: 'statistics', label: 'Statistik', href: '/calendar/statistics' },
];

/** „9 Termine diese Woche · 2 Videoberatungen heute" — the status line of the current week (Beratung 01 header). */
function CurrentWeekSummary() {
  const week = useCalendarWeek(null, null);
  return week.data ? <span className={styles.summary}>{week.data.statusLine}</span> : null;
}

export interface CalendarPageProps {
  tab: CalendarTab;
  /** Dims the page under the appointment panel (Beratung 02, opacity .5). */
  dimmed?: boolean | undefined;
  /** Without the sheet padding (the panel layout of Beratung 02 pads the row itself). */
  flush?: boolean | undefined;
  /** The user pill next to the title; default: the signed-in user. `null` hides it (Beratung 04). */
  pill?: ReactNode | undefined;
  /** The status line at the right of the title row; default: the current week's line. */
  summary?: ReactNode | undefined;
  /** „Verfügbarkeit" / „+ Termin anlegen" in the title row (Beratung 01/05); the archive has none (04). */
  actions?: boolean | undefined;
  /** Where „+ Termin anlegen" starts (`/calendar/new?…`), e.g. with the week's user. */
  newHref?: string | undefined;
  /** Right end of the tab row: week navigation (01), filter pills (04). */
  tabEnd?: ReactNode | undefined;
  children: ReactNode;
}

/** The Beratung page frame (Beratung 01/04, D-51): title row with the user pill and the two buttons, the tab bar, the tab's content. */
export function CalendarPage({
  tab,
  dimmed = false,
  flush = false,
  pill,
  summary,
  actions = true,
  newHref = '/calendar/new',
  tabEnd,
  children,
}: CalendarPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const workspace = useWorkspace();
  const mobile = useIsMobile();
  const shortName = formatShortName(workspace.user.firstName, workspace.user.lastName);
  const pillNode = pill === undefined ? <span className={styles.userPill}>{shortName}</span> : pill;
  const summaryNode = summary === undefined ? <CurrentWeekSummary /> : summary;
  const buttons = (
    <>
      <Button variant="secondary" size="small" onClick={() => navigate('/calendar/availability')}>
        Verfügbarkeit
      </Button>
      <Button size="small" onClick={() => navigate(newHref, { state: { from: location.pathname } })}>
        + Termin anlegen
      </Button>
    </>
  );
  return (
    <div
      className={clsx(styles.page, dimmed && styles.dimmed, flush && styles.flush)}
      aria-hidden={dimmed || undefined}
    >
      <div className={styles.head}>
        <h1 className={styles.title}>Kalender</h1>
        {pillNode}
        {!mobile ? <span className={styles.summaryWrap}>{summaryNode}</span> : null}
        {actions && !mobile ? <span className={styles.headActions}>{buttons}</span> : null}
      </div>
      <div className={styles.tabRow}>
        <Tabs
          label="Kalender"
          variant="bare"
          items={tabs.map((entry) => ({ value: entry.value, label: entry.label }))}
          value={tab}
          onChange={(value) => {
            const target = tabs.find((entry) => entry.value === value);
            if (target) void navigate(target.href);
          }}
          className={styles.tabs}
        />
        {tabEnd && !mobile ? <span className={styles.tabEnd}>{tabEnd}</span> : null}
      </div>
      {mobile && (actions || tabEnd) ? (
        <div className={styles.mobileActions}>
          {tabEnd}
          {actions ? buttons : null}
        </div>
      ) : null}
      <div className={styles.body}>{children}</div>
    </div>
  );
}

/**
 * `/calendar/*` — Beratung tab bar: week (01), archive (04), event types (05/06) with their reminder chain
 * (07), statistics (09); the appointment panel (02/03) and the availability sheet (08).
 */
export function CalendarModule() {
  return (
    <Routes>
      <Route index element={<Navigate to="/calendar/week" replace />} />
      <Route path="week" element={<WeekPage />} />
      <Route path="archive" element={<ArchivePage />} />
      <Route path="statistics" element={<StatisticsPage />} />
      <Route
        path="event-types"
        element={
          <CalendarPage tab="event-types">
            <EventTypesPage />
          </CalendarPage>
        }
      />
      <Route path="event-types/new" element={<EventTypeEditorPage />} />
      <Route path="event-types/:id" element={<EventTypeEditorPage />} />
      <Route path="event-types/:id/reminders" element={<ReminderChainPage />} />
      <Route path="reminders" element={<ReminderChainPage />} />
      <Route path="new" element={<AppointmentPanelPage />} />
      <Route path="availability" element={<AvailabilityPage />} />
      <Route path="*" element={<Navigate to="/calendar/week" replace />} />
    </Routes>
  );
}
